/**
 * Styles and behaviour shared by the Studio's deck controls (kept apart from the components so
 * that the component files export only components).
 */
import { useEffect, useRef } from 'react'

/** A field sized for a panel that shares its height with three others. */
const denseFieldBase =
  'rounded border border-stone-700 bg-black/40 px-2 py-1 text-[11px] text-stone-100 outline-none [color-scheme:dark] focus:border-amber-300/40 disabled:opacity-50'

/** Fills its row. A field that must not fill it uses `denseFieldAuto` and sets its own width. */
export const denseField = `w-full ${denseFieldBase}`
export const denseFieldAuto = denseFieldBase

export const denseLabel = 'text-[9px] uppercase tracking-[0.14em] text-stone-500'

/**
 * Closes on Escape or on a press outside `ref`'s element. Put the ref on the element that holds
 * BOTH the trigger and the panel: a press on the trigger is then "inside", so it toggles rather
 * than closing the panel only to reopen it.
 */
export function useDismiss<T extends HTMLElement>(open: boolean, onClose: () => void) {
  const ref = useRef<T | null>(null)
  useEffect(() => {
    if (!open) return
    const onDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose()
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])
  return ref
}
